/**
 * ScheduleTab - Current status, upcoming schedules, visit history, movement log
 *
 * Movement ledger model: shows directional schedule records (outbound/inbound)
 * with dynamic status banner and presence awareness.
 */

import { useState } from 'react'
import { History, Move, PlaneLanding, PlaneTakeoff, Pencil, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate, formatRelativeDate, getDaysUntil, getHoursSince } from '@/lib/utils/format-business-data'
import { CompactInfoBlock } from './shared/CompactInfoBlock'
import { useAircraftVisitHistory } from '@/hooks/useAircraftVisitHistory'
import { useAircraftMovementLog } from '@/hooks/useAircraftMovementLog'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { ArrivalDialog } from '@/components/ramp-wizard/ArrivalDialog'
import { ScheduleEditDialog } from '@/components/ramp-wizard/ScheduleEditDialog'
import { useUpcomingAircraftSchedules } from '@/hooks/useAircraftSchedules'
import {
  SCHEDULE_CONFIDENCE_LABELS,
  SCHEDULE_CONFIDENCE_VARIANTS,
  SCHEDULE_SOURCE_LABELS,
  SCHEDULE_SOURCE_VARIANTS,
  SCHEDULE_DIRECTION_LABELS,
} from '@/types/aircraft-schedules'
import type { AircraftSchedule } from '@/types/aircraft-schedules'
import type { AllocationEnrichedData } from '@/types/aircraft-card'

interface ScheduleTabProps {
  data: AllocationEnrichedData
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  placed: 'Placed',
  arrived: 'Arrived',
  departed: 'Departed',
  departed_final: 'Left',
  moved: 'Moved',
  unit_changed: 'Relocated',
}

/**
 * Dynamic status banner based on current state (with presence awareness)
 */
function StatusBanner({ data }: { data: AllocationEnrichedData }) {
  const { allocation, leaseContract, transientStay } = data
  const locationParts = [allocation.areaName, allocation.unitCode].filter(Boolean)
  const locationStr = locationParts.length > 0 ? locationParts.join(' · ') : 'Unassigned'

  // Active + Away — aircraft is flying
  if (allocation.presence === 'away' && allocation.status === 'active') {
    if (allocation.expectedReturnAt) {
      const hoursUntil = -getHoursSince(allocation.expectedReturnAt)
      const isOverdue = hoursUntil < 0
      if (isOverdue) {
        return (
          <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            <div className="w-2 h-2 rounded-full bg-red-500 mt-1 shrink-0 animate-pulse" />
            <div className="text-xs leading-relaxed">
              <span className="font-medium">Return overdue</span>
              <span> · Expected {formatRelativeDate(allocation.expectedReturnAt)}</span>
              <span className="text-red-600/70"> ({Math.round(Math.abs(hoursUntil))}h late)</span>
            </div>
          </div>
        )
      }
      return (
        <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
          <div className="w-2 h-2 rounded-full bg-amber-500 mt-1 shrink-0" />
          <div className="text-xs leading-relaxed">
            <span className="font-medium">Away</span>
            <span> · Returns {formatRelativeDate(allocation.expectedReturnAt)}</span>
          </div>
        </div>
      )
    }
    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
        <div className="w-2 h-2 rounded-full bg-amber-500 mt-1 shrink-0" />
        <div className="text-xs leading-relaxed">
          <span className="font-medium">Away</span>
          <span className="text-amber-600/70"> · Return time unknown</span>
        </div>
      </div>
    )
  }

  // Active + Expected — returning soon
  if (allocation.presence === 'expected' && allocation.status === 'active' && allocation.expectedReturnAt) {
    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1 shrink-0" />
        <div className="text-xs leading-relaxed">
          <span className="font-medium">Returning</span>
          <span> · ETA {formatDate(allocation.expectedReturnAt)}</span>
        </div>
      </div>
    )
  }

  // Based in hangar (lease)
  if (leaseContract && leaseContract.status === 'active') {
    const sinceStr = allocation.startsAt
      ? `Since ${formatDate(allocation.startsAt)}`
      : ''
    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
        <div className="w-2 h-2 rounded-full bg-green-500 mt-1 shrink-0" />
        <div className="text-xs leading-relaxed">
          <span className="font-medium">In assigned space</span>
          <span className="text-green-600 dark:text-green-500"> · {locationStr}</span>
          {sinceStr && <span className="text-green-600/70 dark:text-green-500/70"> · {sinceStr}</span>}
        </div>
      </div>
    )
  }

  // Transient checked in
  if (transientStay?.status === 'checked_in') {
    const daysUntilCheckout = getDaysUntil(transientStay.expectedCheckOut)
    const isOverdue = daysUntilCheckout < 0

    if (isOverdue) {
      const hoursOver = Math.round(getHoursSince(transientStay.expectedCheckOut))
      return (
        <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
          <div className="w-2 h-2 rounded-full bg-amber-500 mt-1 shrink-0" />
          <div className="text-xs leading-relaxed">
            <span className="font-medium">Overstaying</span>
            <span> · Expected departure was {formatDate(transientStay.expectedCheckOut)}</span>
            <span className="text-amber-600/70"> ({hoursOver}h ago)</span>
          </div>
        </div>
      )
    }

    const dayNum = transientStay.nightsCount
      ? Math.max(1, Math.ceil((Date.now() - new Date(transientStay.checkIn).getTime()) / (1000 * 60 * 60 * 24)))
      : null

    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
        <div className="w-2 h-2 rounded-full bg-green-500 mt-1 shrink-0" />
        <div className="text-xs leading-relaxed">
          <span className="font-medium">Checked in</span>
          <span className="text-green-600 dark:text-green-500"> · {locationStr}</span>
          {dayNum && transientStay.nightsCount && (
            <span> · Day {dayNum} of {transientStay.nightsCount}</span>
          )}
          <span className="text-green-600/70 dark:text-green-500/70"> · Departing {formatRelativeDate(transientStay.expectedCheckOut)}</span>
        </div>
      </div>
    )
  }

  // Transient booked (reserved/confirmed)
  if (transientStay && (transientStay.status === 'reserved' || transientStay.status === 'confirmed')) {
    return (
      <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1 shrink-0" />
        <div className="text-xs leading-relaxed">
          <span className="font-medium">Booked</span>
          <span> · Expected {formatDate(transientStay.checkIn)}</span>
          {allocation.unitCode && <span> · Space: {allocation.unitCode}</span>}
        </div>
      </div>
    )
  }

  // No contract — on ramp with no status
  return (
    <div className="flex items-start gap-2 px-2 py-1.5 rounded bg-muted text-muted-foreground">
      <div className="w-2 h-2 rounded-full bg-muted-foreground/50 mt-1 shrink-0" />
      <div className="text-xs leading-relaxed">
        <span className="font-medium">On field</span>
        <span> · {locationStr}</span>
        {allocation.startsAt && (
          <span className="text-muted-foreground/70"> · Since {formatDate(allocation.startsAt)}</span>
        )}
      </div>
    </div>
  )
}

/**
 * Upcoming schedules section (uses outbound aircraft_schedules with edit/fulfill buttons)
 */
function UpcomingSchedules({ aircraftId, locationId }: { aircraftId: string; locationId: string | null }) {
  const { data: schedules, isLoading } = useUpcomingAircraftSchedules(aircraftId)
  const [editSchedule, setEditSchedule] = useState<AircraftSchedule | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const titleAction = (
    <button
      onClick={() => setCreateOpen(true)}
      className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors"
      title="Add schedule"
    >
      <Plus className="w-3.5 h-3.5 text-muted-foreground" />
    </button>
  )

  if (isLoading) {
    return (
      <CompactInfoBlock title="Upcoming Schedules" titleAction={titleAction}>
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full mt-1" />
      </CompactInfoBlock>
    )
  }

  if (!schedules || schedules.length === 0) {
    return (
      <>
        <CompactInfoBlock title="Upcoming Schedules" titleAction={titleAction}>
          <p className="text-xs text-muted-foreground/50 text-center py-2">
            No upcoming schedules
          </p>
        </CompactInfoBlock>

        <ScheduleEditDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          schedule={null}
          aircraftId={aircraftId}
          locationId={locationId ?? undefined}
        />
      </>
    )
  }

  return (
    <>
      <CompactInfoBlock title="Upcoming Schedules" titleAction={titleAction}>
        <div className="space-y-1.5">
          {schedules.map((sched) => (
            <div
              key={sched.id}
              className="flex items-center justify-between text-xs py-1 border-b border-border/30 last:border-0"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {sched.direction === 'inbound' ? (
                  <PlaneLanding className="w-3 h-3 text-blue-600 shrink-0" />
                ) : (
                  <PlaneTakeoff className="w-3 h-3 text-orange-600 shrink-0" />
                )}
                {sched.airportIcao ? (
                  <span className="font-medium">{sched.airportIcao}</span>
                ) : (
                  <span className="text-muted-foreground italic">
                    {sched.direction === 'inbound' ? 'No origin' : 'No dest.'}
                  </span>
                )}
                <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                  {SCHEDULE_DIRECTION_LABELS[sched.direction]}
                </Badge>
                {sched.source && (
                  <Badge
                    variant={SCHEDULE_SOURCE_VARIANTS[sched.source] ?? 'outline'}
                    className="text-[9px] px-1 py-0 h-4"
                  >
                    {SCHEDULE_SOURCE_LABELS[sched.source] ?? sched.source}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-muted-foreground">
                  {formatDate(sched.scheduledAt)}
                </span>
                {sched.confidence && (
                  <Badge
                    variant={SCHEDULE_CONFIDENCE_VARIANTS[sched.confidence] ?? 'outline'}
                    className="text-[9px] px-1 py-0 h-4"
                  >
                    {SCHEDULE_CONFIDENCE_LABELS[sched.confidence] ?? sched.confidence}
                  </Badge>
                )}
                <button
                  onClick={() => setEditSchedule(sched)}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted transition-colors"
                  title="Edit schedule"
                >
                  <Pencil className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </CompactInfoBlock>

      {/* Schedule Edit Dialog (edit mode) */}
      {editSchedule && (
        <ScheduleEditDialog
          open={!!editSchedule}
          onOpenChange={(open) => { if (!open) setEditSchedule(null) }}
          schedule={editSchedule}
        />
      )}

      {/* Schedule Edit Dialog (create mode) */}
      <ScheduleEditDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        schedule={null}
        aircraftId={aircraftId}
        locationId={locationId ?? undefined}
      />
    </>
  )
}

/**
 * Visit history section (past transient stays)
 */
function VisitHistory({ aircraftId }: { aircraftId: string }) {
  const { data: visits, isLoading } = useAircraftVisitHistory(aircraftId)

  if (isLoading) {
    return (
      <CompactInfoBlock title="Visit History">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full mt-1" />
      </CompactInfoBlock>
    )
  }

  if (!visits || visits.length === 0) {
    return (
      <CompactInfoBlock title="Visit History">
        <p className="text-xs text-muted-foreground/50 text-center py-2">
          No past visits
        </p>
      </CompactInfoBlock>
    )
  }

  return (
    <CompactInfoBlock title="Visit History">
      <div className="space-y-1.5">
        {visits.map((visit) => {
          const location = [visit.areaName, visit.unitCode].filter(Boolean).join(' · ')
          return (
            <div
              key={visit.id}
              className="flex items-center justify-between text-xs py-1 border-b border-border/30 last:border-0"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <History className="w-3 h-3 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground truncate">
                  {formatDate(visit.checkIn)} — {formatDate(visit.checkOut)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {visit.nightsCount && (
                  <span className="text-muted-foreground">{visit.nightsCount}n</span>
                )}
                {location && (
                  <span className="text-[10px] text-muted-foreground/70">{location}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </CompactInfoBlock>
  )
}

/**
 * Movement log section (allocation events)
 */
function MovementLog({ aircraftId }: { aircraftId: string }) {
  const { data: movements, isLoading } = useAircraftMovementLog(aircraftId)

  if (isLoading) {
    return (
      <CompactInfoBlock title="Movement Log">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full mt-1" />
      </CompactInfoBlock>
    )
  }

  if (!movements || movements.length === 0) {
    return (
      <CompactInfoBlock title="Movement Log">
        <p className="text-xs text-muted-foreground/50 text-center py-2">
          No movement history
        </p>
      </CompactInfoBlock>
    )
  }

  return (
    <CompactInfoBlock title="Movement Log">
      <div className="space-y-1.5">
        {movements.map((entry) => {
          const location = [entry.areaName, entry.unitCode].filter(Boolean).join(' · ')
          return (
            <div
              key={entry.id}
              className="flex items-center justify-between text-xs py-1 border-b border-border/30 last:border-0"
            >
              <div className="flex items-center gap-1.5">
                <Move className="w-3 h-3 text-muted-foreground" />
                <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                  {EVENT_TYPE_LABELS[entry.eventType] || entry.eventType}
                </Badge>
                {location && (
                  <span className="text-muted-foreground">{location}</span>
                )}
              </div>
              <span className="text-muted-foreground text-[10px]">
                {formatDate(entry.eventAt)}
              </span>
            </div>
          )
        })}
      </div>
    </CompactInfoBlock>
  )
}

export function ScheduleTab({ data }: ScheduleTabProps) {
  const aircraftId = data.aircraft.id
  const { locationId } = useAppContext()
  const [arrivalOpen, setArrivalOpen] = useState(false)
  const isAway = data.allocation.presence === 'away'

  return (
    <div className="space-y-0">
      {/* Status Banner */}
      <div className="py-2">
        <StatusBanner data={data} />
        {isAway && (
          <div className="mt-2 px-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-3 text-xs w-full"
              onClick={() => setArrivalOpen(true)}
            >
              <PlaneLanding className="w-3.5 h-3.5 mr-1.5" />Mark Returned
            </Button>
          </div>
        )}
      </div>

      {/* Arrival Dialog */}
      {locationId && isAway && (
        <ArrivalDialog
          open={arrivalOpen}
          onOpenChange={setArrivalOpen}
          tailNumber={data.aircraft.tailNumber}
          allocationId={data.allocationId}
          aircraftId={data.aircraft.id}
          locationId={locationId}
        />
      )}

      {/* Divider */}
      <div className="border-t border-border/50" />

      {/* Transient current stay details */}
      {data.transientStay && data.transientStay.status === 'checked_in' && (
        <>
          <CompactInfoBlock title="Current Stay">
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Booking</span>
                <span className="font-medium font-mono">{data.transientStay.bookingNumber}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Check-In</span>
                <span>{formatDate(data.transientStay.checkIn)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Check-Out</span>
                <span className={getDaysUntil(data.transientStay.expectedCheckOut) < 0 ? 'text-red-600 font-medium' : ''}>
                  {formatDate(data.transientStay.expectedCheckOut)}
                  <span className="text-muted-foreground ml-1">
                    ({formatRelativeDate(data.transientStay.expectedCheckOut)})
                  </span>
                </span>
              </div>
              {data.transientStay.actualCheckOut && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Actual</span>
                  <span>{formatDate(data.transientStay.actualCheckOut)}</span>
                </div>
              )}
            </div>
          </CompactInfoBlock>
          <div className="border-t border-border/50" />
        </>
      )}

      {/* Upcoming Schedules */}
      <UpcomingSchedules aircraftId={aircraftId} locationId={locationId} />

      <div className="border-t border-border/50" />

      {/* Visit History */}
      <VisitHistory aircraftId={aircraftId} />

      <div className="border-t border-border/50" />

      {/* Movement Log */}
      <MovementLog aircraftId={aircraftId} />
    </div>
  )
}
